/**
 * usePublicShareHydration
 *
 * Bridges the public share payload into the global store so the authenticated
 * map pipeline (UnifiedAnchorSymbolLayer, TaskGeometryOverlays, hover
 * previews, detail tabs) renders public data unchanged:
 *
 * - enters public-share mode (interaction hooks disable editing affordances),
 * - publishes the share's basemap allowlist via `map.publicBasemapContext`,
 * - hydrates `tasks.mapTasks` / `notes.mapNotes` / `obstructions.mapItems`
 *   with the slim public MAP items mapped to the app types.
 *
 * Everything is cleared on unmount so a later authenticated session starts
 * from a clean store.
 *
 * @module components/share/public/usePublicShareHydration
 */
import { useEffect } from 'react';

import { useGeoMarkupStore } from '@/store';
import { clampBasemapToAllowed, isValidBasemapId } from '@/config/maplibre';
import type { BasemapId } from '@/config/maplibre';
import {
  mapPublicNoteForMap,
  mapPublicObstructionForMap,
  mapPublicTaskForMap,
} from '@/store/mappers/publicShareMapper';
import type { PublicShareConfig, PublicShareMapData } from '@/types/share';

/** Prefix of the synthetic in-memory basemap-preference key for public share pages. */
const SHARE_BASEMAP_KEY_PREFIX = 'share:';

/** Prefix of the localStorage key persisting a viewer's basemap pick per share. */
const SHARE_BASEMAP_PERSIST_PREFIX = 'share-basemap:';

/** Builds the synthetic per-share basemap preference key (in-memory store). */
export function buildShareBasemapKey(token: string): string {
  return `${SHARE_BASEMAP_KEY_PREFIX}${token}`;
}

/** Builds the localStorage key persisting a viewer's basemap pick for a share. */
export function buildShareBasemapPersistKey(token: string): string {
  return `${SHARE_BASEMAP_PERSIST_PREFIX}${token}`;
}

/**
 * Hydrates the store for a public share page. Pass the config/map payload from
 * `usePublicShare` — both may be null while loading or password-gated.
 */
export function usePublicShareHydration(
  token: string,
  config: PublicShareConfig | null,
  mapData: PublicShareMapData | null
): void {
  const enterPublicShare = useGeoMarkupStore(
    (state) => state.publicShare.enterPublicShare
  );
  const exitPublicShare = useGeoMarkupStore(
    (state) => state.publicShare.exitPublicShare
  );
  const setPublicBasemapContext = useGeoMarkupStore(
    (state) => state.map.setPublicBasemapContext
  );
  const setMapTasks = useGeoMarkupStore((state) => state.tasks.setMapTasks);
  const setMapNotes = useGeoMarkupStore((state) => state.notes.setMapNotes);
  const setMapItems = useGeoMarkupStore(
    (state) => state.obstructions.setMapItems
  );
  const setCustomLayerVisible = useGeoMarkupStore(
    (state) => state.mapLayers.setCustomLayerVisible
  );

  useEffect(() => {
    enterPublicShare(token);

    return () => {
      exitPublicShare();
      setPublicBasemapContext(null);
      setMapTasks([]);
      setMapNotes([]);
      setMapItems([]);
    };
  }, [
    token,
    enterPublicShare,
    exitPublicShare,
    setPublicBasemapContext,
    setMapTasks,
    setMapNotes,
    setMapItems,
  ]);

  useEffect(() => {
    if (!config) return;

    const allowedBasemaps = config.allowedBasemaps.filter(
      isValidBasemapId
    ) as BasemapId[];

    // Owner-configured start basemap; only honoured when still allow-listed.
    const defaultBasemap = clampBasemapToAllowed(
      config.defaultBasemap,
      allowedBasemaps
    );

    setPublicBasemapContext({
      shareKey: buildShareBasemapKey(token),
      allowedBasemaps,
      defaultBasemap,
    });

    // Custom layers start visible; the public layers panel toggles them per visit.
    for (const layer of config.sharedCustomLayers) {
      setCustomLayerVisible(layer.id, true);
    }
  }, [config, token, setPublicBasemapContext, setCustomLayerVisible]);

  useEffect(() => {
    if (!mapData) return;

    setMapTasks(mapData.tasks.map(mapPublicTaskForMap));
    setMapNotes(mapData.notes.map(mapPublicNoteForMap));
    setMapItems(mapData.obstructions.map(mapPublicObstructionForMap));
  }, [mapData, setMapTasks, setMapNotes, setMapItems]);
}
